import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { useLocale, pickLocalized } from "@/contexts/LocaleContext";
import { Calendar, ArrowRight, Clock } from "lucide-react";

function formatDateTime(iso, locale) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(locale === "hr" ? "hr-HR" : "en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Zagreb",
    }).format(d);
  } catch {
    return iso;
  }
}

export default function Events() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [items, setItems] = useState([]);
  const [past, setPast] = useState([]);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    api.get("/public/events").then((r) => setItems(r.data || []));
    api.get("/public/events", { params: { past: true } }).then((r) => setPast(r.data || []));
  }, []);

  return (
    <PublicLayout pageTitle={t("events_page.title")} testid="events-page">
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-primary/80">— {t("nav.events")}</p>
        <h1 className="font-display text-5xl sm:text-6xl mt-3" data-testid="events-title">
          {t("events_page.title")}
        </h1>
        <p className="text-muted-foreground mt-3 max-w-xl">{t("events_page.subtitle")}</p>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-12">
        {items.length === 0 ? (
          <div className="border border-dashed border-border rounded-xl p-16 text-center text-muted-foreground" data-testid="events-empty">
            <Calendar className="w-8 h-8 mx-auto mb-3 opacity-60" />
            {t("common.coming_soon")}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
            {items.map((ev) => (
              <Link
                to={`/events/${ev.slug}`}
                key={ev.id}
                className="group border border-border rounded-2xl bg-card overflow-hidden hover:border-primary/40 transition"
                data-testid={`event-card-${ev.slug}`}
              >
                <div className="aspect-[16/9] relative bg-gradient-to-br from-primary/10 via-background to-secondary overflow-hidden">
                  {ev.graphic_url && (
                    <img
                      src={ev.graphic_url}
                      alt={pickLocalized(ev.title, locale)}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                    />
                  )}
                  {ev.featured && (
                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.25em] px-2 py-1 rounded-full bg-primary text-primary-foreground">
                      Featured
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs text-primary">
                    <Clock className="w-3 h-3" /> {formatDateTime(ev.starts_at, locale)}
                  </div>
                  <h3 className="font-display text-2xl mt-2">{pickLocalized(ev.title, locale)}</h3>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mt-2">
                    {ev.booking_mode === "ticket_and_table"
                      ? `${t("nav.events")} · €${Number(ev.ticket_price).toFixed(2)}`
                      : ev.booking_mode === "table_only"
                      ? t("nav.booking")
                      : ""}
                  </div>
                  <div className="inline-flex items-center gap-1 text-primary text-sm mt-4">
                    Detalji <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {past.length > 0 && (
          <div className="mt-16">
            <button
              type="button"
              onClick={() => setShowPast(!showPast)}
              className="text-xs uppercase tracking-[0.3em] text-muted-foreground hover:text-primary"
              data-testid="events-past-toggle"
            >
              {showPast ? "Sakrij prošla / Hide past" : "Prošla događanja / Past events"} ({past.length})
            </button>
            {showPast && (
              <ul className="mt-4 divide-y divide-border border border-border rounded-xl bg-card">
                {past.map((ev) => (
                  <li key={ev.id} className="px-4 py-3 flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="flex-1 text-sm">{pickLocalized(ev.title, locale)}</span>
                    <span className="text-xs text-muted-foreground">{formatDateTime(ev.starts_at, locale)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
